// =============================================================================
// Layer 2 — pattern classifiers.
//
// Takes the threshold-zone output from Layer 1 + the raw inputs and determines
// which of the 13 substantive patterns fire. Meta-patterns (mixed_picture,
// insufficient_data) are handled by Layer 3 — not here.
//
// Each pattern is a predicate: (eval, inputs) => boolean. Returns true when
// the pattern's YAML-defined triggers match the user's data.
//
// Pattern IDs match decision-logic.yaml exactly. If you add / remove / rename
// a pattern, update both this file AND the YAML AND pattern-narratives.md.
// =============================================================================

import { isInflammationFlagged } from './classify.js';
import type { ThresholdEvaluation } from './thresholds.js';
import type { LabInputs } from './types.js';

/** One predicate per substantive pattern. 15 total. */
export type PatternId =
  | 'stage_0_iron_replete'
  | 'stage_1_incipient_id'
  | 'stage_2_iron_depletion_no_anemia'
  | 'stage_3_iron_deficient_erythropoiesis'
  | 'stage_4_iron_deficiency_anemia'
  | 'inflammation_masks_iron_status'
  | 'functional_iron_deficiency'
  | 'anemia_of_chronic_disease_classic'
  | 'iron_overload_trigger_premenopausal'
  | 'iron_overload_trigger_postmenopausal'
  | 'very_high_ferritin_any_stage'
  | 'microcytic_normal_iron_studies'
  | 'macrocytic_anemia_signal'
  | 'anemia_with_replete_ferritin'
  | 'pregnancy_t1_at_risk_for_t3_deficiency';

export type PatternTrigger = (
  evaluation: ThresholdEvaluation,
  inputs: LabInputs,
) => boolean;

// -----------------------------------------------------------------------------
// Helper: "inflammation clear" for patterns that require it
// -----------------------------------------------------------------------------

const inflammationClear = (inputs: LabInputs): boolean => !isInflammationFlagged(inputs);

// -----------------------------------------------------------------------------
// Helper: chronic inflammatory context (proxy for Layer-2 pattern triggers that
// require ongoing inflammation, not just acute illness)
// -----------------------------------------------------------------------------

const hasInflammationContext = (inputs: LabInputs): boolean => {
  // User-reported chronic inflammatory condition OR current CRP elevation OR
  // recent-illness flag. Any of these signals "inflammation is in the picture."
  // (Compared to the acute inflammation_clear check above, this is a softer
  // condition matching the YAML's `and_any_of` pattern.)
  if (isInflammationFlagged(inputs)) return true;
  // Phase-1 TODO: add user input for "chronic inflammatory condition" when
  // Step 2c wizard copy is wired into the form.
  return false;
};

// -----------------------------------------------------------------------------
// Part 1 — Iron staged model
// -----------------------------------------------------------------------------

export const stage_0_iron_replete: PatternTrigger = (ev, inputs) => {
  if (ev.ferritinZone !== 'stage_0_replete') return false;
  if (ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference') return false;
  if (!inflammationClear(inputs)) return false;
  return true;
};

export const stage_1_incipient_id: PatternTrigger = (ev, inputs) => {
  if (ev.ferritinZone !== 'stage_1_incipient') return false;
  if (ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference') return false;
  if (!inflammationClear(inputs)) return false;
  return true;
};

export const stage_2_iron_depletion_no_anemia: PatternTrigger = (ev, inputs) => {
  if (ev.ferritinZone !== 'stage_2_depletion') return false;
  if (ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference') return false;
  if (!inflammationClear(inputs)) return false;
  return true;
};

/**
 * Stage 3 IDE — iron stores low enough to restrict erythropoiesis, Hb still
 * in reference range. Per PRD-01 sect 2.7 + Mei 2024 IPD analysis, this is the
 * "ferritin < 25 ng/mL with non-anemic Hb" state. The engine's threshold
 * machinery splits very-low ferritin (< 15) into a separate `stage_4_ida_range`
 * zone for stage-4-IDA-pattern reuse, but clinically that internal split is
 * an artifact: ferritin of 8 with non-anemic Hb is still Stage 3 IDE
 * (just a more severe instance of the same iron-restricted-erythropoiesis
 * state). Accept both zones to avoid the engine-internal split leaving very-
 * low-ferritin + non-anemic users in insufficient_data.
 */
export const stage_3_iron_deficient_erythropoiesis: PatternTrigger = (ev, inputs) => {
  if (ev.ferritinZone !== 'stage_3_ide' && ev.ferritinZone !== 'stage_4_ida_range') return false;
  if (ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference') return false;
  if (!inflammationClear(inputs)) return false;
  return true;
};

/**
 * Stage 4 IDA — low ferritin + low Hb. Trigger paths:
 *   Primary: ferritin < stage_3_ide_upper AND hb below reference.
 *   Fallback A (TSAT confirms): no ferritin + MCV microcytic + Hb below
 *     + TSAT < 20 + inflammation clear.
 *   Fallback B (presumptive, CBC only): no ferritin + no TSAT + MCV
 *     microcytic + Hb below + RDW elevated + inflammation clear. RDW
 *     discriminates IDA from thalassemia trait (high RDW → IDA; normal RDW
 *     → thalassemia suggestive). Classified at low confidence; narrative
 *     says ferritin would confirm.
 */
export const stage_4_iron_deficiency_anemia: PatternTrigger = (ev, inputs) => {
  if (!inflammationClear(inputs)) return false;

  const hbBelow = ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference';
  if (!hbBelow) return false;

  // Primary path: ferritin in the deficient range. PRD-01 sect 2.7 defines
  // Stage 4 IDA as "ferritin < 30 + Hb below reference." Three zones map
  // into < 30: stage_2_depletion (25-30), stage_3_ide (15-25 in the engine's
  // internal split), stage_4_ida_range (< 15). All three should fire IDA
  // when paired with anemia — the staging zones are an internal threshold
  // artifact, not separate clinical states.
  const ferritinDeficient =
    ev.ferritinZone === 'stage_2_depletion'
    || ev.ferritinZone === 'stage_3_ide'
    || ev.ferritinZone === 'stage_4_ida_range';
  if (ferritinDeficient) return true;

  // Fallback C: borderline ferritin (stage_1_incipient zone, 30-50) +
  // anemia + microcytic + low TSAT. The "ferritin lag" phenomenon —
  // Mei 2024 IPD analysis + Lahtiharju 2025 acknowledge that ferritin
  // can sit just above the < 30 IDA threshold while supplementary markers
  // (TSAT < 20 + microcytic MCV) confirm iron-restricted erythropoiesis.
  // Without this fallback the engine misroutes textbook IDA to
  // insufficient_data when ferritin is borderline.
  if (
    inputs.ferritin !== undefined
    && ev.ferritinZone === 'stage_1_incipient'
    && ev.mcvZone === 'microcytic'
    && ev.tsatZone === 'low'
  ) {
    return true;
  }

  const noFerritin = inputs.ferritin === undefined;
  if (!noFerritin) return false;

  // Fallback A: no ferritin but TSAT confirms low delivery.
  if (ev.mcvZone === 'microcytic' && ev.tsatZone === 'low') {
    return true;
  }

  // Fallback B: CBC-only presumptive IDA. RDW elevation discriminates IDA
  // from hemoglobinopathy trait (Mentzer / RDW literature: high RDW is
  // characteristic of IDA).
  const noTsat = inputs.tsat === undefined;
  if (
    noTsat &&
    ev.mcvZone === 'microcytic' &&
    ev.rdwZone === 'elevated'
  ) {
    return true;
  }

  return false;
};

// -----------------------------------------------------------------------------
// Part 2 — Inflammation patterns
// -----------------------------------------------------------------------------

/**
 * Fires when inflammation is in the picture. Not mutually exclusive with
 * other patterns — it travels WITH them as a caveat layer per the
 * inflammation_caveat_applied arbitration rule. Classifying this pattern
 * means the engine will demote ferritin-based conclusions.
 */
export const inflammation_masks_iron_status: PatternTrigger = (_ev, inputs) => {
  return isInflammationFlagged(inputs);
};

/**
 * Functional iron deficiency: iron is present but locked away.
 * Ferritin normal/high + TSAT low + inflammation. NOT anemic by spec.
 * When anemia is present, arbitration rule acd_supersedes_functional_id_when_anemic
 * promotes ACD classic instead (handled in Layer 3).
 */
export const functional_iron_deficiency: PatternTrigger = (ev, inputs) => {
  if (inputs.ferritin === undefined || inputs.ferritin < 100) return false;
  if (ev.tsatZone !== 'low') return false;
  if (!hasInflammationContext(inputs)) return false;
  return true;
};

/**
 * Anemia of chronic disease (classic). Hb below reference + ferritin ≥ 30
 * (not depleted) + TSAT low + inflammation context.
 */
export const anemia_of_chronic_disease_classic: PatternTrigger = (ev, inputs) => {
  const hbBelow = ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference';
  if (!hbBelow) return false;
  if (inputs.ferritin === undefined || inputs.ferritin < 30) return false;
  if (ev.tsatZone !== 'low') return false;
  if (!hasInflammationContext(inputs)) return false;
  return true;
};

// -----------------------------------------------------------------------------
// Part 3 — Overload patterns
// -----------------------------------------------------------------------------

export const iron_overload_trigger_premenopausal: PatternTrigger = (ev, inputs) => {
  if (inputs.lifeStage === 'postmenopausal') return false;
  if (!ev.isOverloadTriggerFired) return false;
  // very_high_ferritin takes its own pattern; don't double-count.
  if (ev.ferritinZone === 'very_high') return false;
  return true;
};

export const iron_overload_trigger_postmenopausal: PatternTrigger = (ev, inputs) => {
  if (inputs.lifeStage !== 'postmenopausal') return false;
  if (!ev.isOverloadTriggerFired) return false;
  if (ev.ferritinZone === 'very_high') return false;
  return true;
};

export const very_high_ferritin_any_stage: PatternTrigger = (ev) => {
  return ev.ferritinZone === 'very_high';
};

// -----------------------------------------------------------------------------
// Part 4 — Morphology patterns
// -----------------------------------------------------------------------------

/**
 * Microcytic anemia with normal/elevated iron studies. Routes to hemoglobin
 * electrophoresis for thalassemia evaluation. Distinct from IDA (which has
 * low iron) — the iron studies being adequate is the diagnostic tell.
 */
export const microcytic_normal_iron_studies: PatternTrigger = (ev, inputs) => {
  if (ev.mcvZone !== 'microcytic') return false;
  // Iron studies adequate = ferritin not depleted + TSAT not low.
  if (inputs.ferritin === undefined || inputs.ferritin < 30) return false;
  if (ev.tsatZone === 'low') return false;
  return true;
};

/**
 * Macrocytic anemia signal — B12 / folate differential. Out of FF's primary
 * iron scope; routes to clinician.
 */
export const macrocytic_anemia_signal: PatternTrigger = (ev) => {
  if (ev.mcvZone !== 'macrocytic') return false;
  const hbBelow = ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference';
  if (!hbBelow) return false;
  return true;
};

/**
 * Anemia with replete ferritin, undifferentiated.
 *
 * Anemia is present (Hb below the life-stage reference) but ferritin sits at
 * or above the depletion threshold (≥ 30 μg/L) — iron deficiency does not
 * explain the anemia. Without MCV (morphology) or TSAT (iron-utilization),
 * the engine cannot differentiate between several plausible causes:
 *   - Anemia of chronic disease (would be confirmed by low TSAT + inflammation)
 *   - B12 / folate deficiency (would be confirmed by macrocytic MCV)
 *   - Hemolysis or post-bleed in transition (would show RDW + retic shifts)
 *   - Hemoglobinopathy / thalassemia trait (would be confirmed by microcytic MCV)
 *
 * This pattern surfaces a "ferritin doesn't explain this — let's get the
 * marker that does" routing, with `mostInformativeNextTest = 'mcv'`.
 *
 * Evidence base: clinical-hematology consensus that anemia + non-deficient
 * ferritin is a discrete diagnostic crossroads requiring MCV-based subtype
 * differentiation (WHO 2024 anemia guidelines; the Mei 2024 IPD methodology
 * the tool already cites for ferritin thresholds).
 *
 * Conditions for firing:
 *   1. Ferritin entered (we need the value to know it's not deficient).
 *   2. Hemoglobin below life-stage reference range (anemia present).
 *   3. Ferritin in stage_0_replete OR stage_1_incipient zone (≥ 30 μg/L).
 *   4. No MCV entered — morphology patterns would handle micro/macrocytic.
 *   5. No TSAT entered — ACD / FID patterns would handle iron-locked-away.
 *   6. Inflammation clear — inflammation_masks would handle the reading.
 *
 * Without those guards, this pattern would race other classifiers; with them,
 * it fires only in the genuinely undifferentiated "Hb + ferritin only" case.
 */
export const anemia_with_replete_ferritin: PatternTrigger = (ev, inputs) => {
  if (inputs.ferritin === undefined) return false;

  const hbBelow = ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference';
  if (!hbBelow) return false;

  // ferritinReplete includes:
  //   - stage_0_replete / stage_1_incipient (5-zone non-pregnant model)
  //   - elevated_normal (single-threshold model used by pregnancy /
  //     postpartum / peri / postmenopausal — "above the life-stage
  //     deficiency cutoff but the corpus does not subdivide above it").
  // The pattern's whole purpose is "ferritin doesn't explain this anemia"
  // — elevated_normal is exactly that situation in non-NPP life stages.
  const ferritinReplete =
    ev.ferritinZone === 'stage_0_replete'
    || ev.ferritinZone === 'stage_1_incipient'
    || ev.ferritinZone === 'elevated_normal';
  if (!ferritinReplete) return false;

  // Skip when a more-specific morphology pattern owns the read:
  //   - macrocytic_anemia_signal owns macrocytic always (B12/folate
  //     differential)
  //   - microcytic_normal_iron_studies owns microcytic ONLY when TSAT is
  //     not low (its gating condition — "iron studies adequate" requires
  //     TSAT not low). When TSAT IS low + microcytic + ferritin replete,
  //     microcytic_normal does NOT fire, and this pattern handles the
  //     remainder: ferritin doesn't explain the anemia even though the
  //     supplementary markers point toward iron restriction. Common
  //     clinical interpretation: ACD without recognized inflammation,
  //     or acute-phase ferritin elevation masking true iron deficiency.
  if (ev.mcvZone === 'macrocytic') return false;
  if (ev.mcvZone === 'microcytic' && ev.tsatZone !== 'low') return false;

  // TSAT presence is permitted: low TSAT informs the differential (could be
  // functional iron deficiency or ACD masked by ferritin) but doesn't change
  // the "iron stores aren't the bottleneck" framing. The pattern's QPL and
  // narrative explicitly cover TSAT-low-with-replete-ferritin scenarios.

  if (!inflammationClear(inputs)) return false;

  return true;
};

/**
 * Pregnancy first-trimester at-risk-for-T3-deficiency pattern.
 *
 * McCarthy 2024 (prospective high-resource pregnancy cohort): a ferritin
 * value at ~15 weeks of gestation that sits ABOVE the trimester-specific
 * IDA cutoff (Mei 2024 T1 ≥ 25.8 μg/L) but BELOW 60 μg/L predicts iron
 * deficiency in the third trimester. Patients in this band are
 * appropriate candidates for preventive intervention before they cross
 * into deficiency.
 *
 * Evidence strength: PRELIMINARY (single prospective cohort, 98%
 * Caucasian, not yet replicated in a second cohort). Surfaced as a named
 * pattern because the action implication is meaningful — this is the
 * window where preventive supplementation is best supported.
 *
 * Conditions for firing:
 *   1. Life stage is pregnant_t1.
 *   2. Ferritin entered, in [25.8, 60) μg/L.
 *   3. Hemoglobin not below T1 anemia threshold (110 g/L) — when anemic,
 *      Stage 4 IDA fires instead.
 *   4. Inflammation clear — otherwise inflammation_masks owns the read.
 */
export const pregnancy_t1_at_risk_for_t3_deficiency: PatternTrigger = (ev, inputs) => {
  if (inputs.lifeStage !== 'pregnant_t1') return false;
  if (inputs.ferritin === undefined) return false;
  if (inputs.ferritin < 25.8 || inputs.ferritin >= 60) return false;
  // Hemoglobin not anemic (T1 threshold 110 g/L).
  if (ev.hemoglobinZone !== undefined && ev.hemoglobinZone !== 'within_reference') return false;
  if (!inflammationClear(inputs)) return false;
  return true;
};

// -----------------------------------------------------------------------------
// Registry + evaluator
// -----------------------------------------------------------------------------

/**
 * All 15 substantive pattern classifiers, keyed by pattern id.
 * Meta-patterns (mixed_picture, insufficient_data, no_established_pattern)
 * are not in this registry — they're synthesized at Layer 3 based on the
 * output of this layer.
 */
export const PATTERN_TRIGGERS: Record<PatternId, PatternTrigger> = {
  stage_0_iron_replete,
  stage_1_incipient_id,
  stage_2_iron_depletion_no_anemia,
  stage_3_iron_deficient_erythropoiesis,
  stage_4_iron_deficiency_anemia,
  inflammation_masks_iron_status,
  functional_iron_deficiency,
  anemia_of_chronic_disease_classic,
  iron_overload_trigger_premenopausal,
  iron_overload_trigger_postmenopausal,
  very_high_ferritin_any_stage,
  microcytic_normal_iron_studies,
  macrocytic_anemia_signal,
  anemia_with_replete_ferritin,
  pregnancy_t1_at_risk_for_t3_deficiency,
};

/** Evaluate all patterns; return the ids that fire. Layer 3 arbitrates. */
export function evaluatePatterns(
  evaluation: ThresholdEvaluation,
  inputs: LabInputs,
): PatternId[] {
  const fired: PatternId[] = [];
  for (const [id, trigger] of Object.entries(PATTERN_TRIGGERS) as [PatternId, PatternTrigger][]) {
    if (trigger(evaluation, inputs)) fired.push(id);
  }
  return fired;
}
